import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import HasPermission from "components/HasPermission";
import {
    Button,
    Card,
    CardBody,
    CardFooter,
    CardHeader,
    Col,
    Collapse,
    Container,
    Input,
    Row,
    FormGroup,
} from "reactstrap";
import Select from "react-select";
import Header from "components/Headers/Header";
import { toast } from "react-toastify";
import {
    platformList,
    updatePlatformStatus,
    getPlatformById,
} from "utils/endpoint";
import UseFullPageLoader from "utils/UseFullPageLoader";
import { isActive, publisherType as countryType } from "utils/enums";
import { Pagination as Paginationes } from "antd";
import { parseURLParams } from "views/universe/UniverseList";
import { backToPreviousClickedRecord } from "utils/Commen";
import useSearchParams from "utils/useSearchParams";
import { handleFilterOnKeyEnter } from "views/media/lightNovel/LightNovelList";

function PlatformList() {
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const id = params.id;
    const currentPage = params.currentPage;
    const fromDate = params.fromDate;
    const toDate = params.toDate;
    const name = params.name;
    const country = params.country;
    const is_active = params.is_active;
    const [isLoading, setIsLoading] = useState(false);
    const [filterLoading, setFilterLoading] = useState(false);
    const [platformLists, setPlatformLists] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [page, setPage] = useState(currentPage || 1);
    const [per_Page, setPer_Page] = useState(100);
    const [isRedirectFirstPage, setIsRedirectFirstPage] = useState(1)
    const [isOpen, setIsOpen] = React.useState(false);
    const [firstTimeFilterActive, setFirstTimeFilterActive] = useState(false);
    const [filterFromDate, setFilterFromDate] = useState(fromDate || "");
    const [filterToDate, setFilterToDate] = useState(toDate || "");
    const [filterName, setFilterName] = useState(name || "");
    const [filterIsActive, setFilterIsActive] = useState(is_active || "");
    const [filterCountry, setFilterCountry] = useState(country || "");
    const [permissions, setPermissions] = useState([]);
    const [loader, showLoader, hideLoader] = UseFullPageLoader();
    const [platformFilter, setPlatformFilter] = useState('')
    const selectInputRef = useRef(null);
    const selectCountryInputRef = useRef(null);
    const toggle = () => setIsOpen(!isOpen);
    const [sort_key, setSortKey] = useState("created_at");
    const [sort_by, setSortBy] = useState("DESC");

    const sortColumns = (currentSortKey, currentSortBy) => {
        let mySortKey = currentSortKey;
        let mySortBy = currentSortBy;
        if (!sort_by) {
            mySortKey = currentSortKey;
            mySortBy = "ASC";
            setSortKey(currentSortKey);
            setSortBy("ASC");
        }
        if (currentSortKey === sort_key) {
            mySortKey = currentSortKey;
            mySortBy = currentSortBy === "ASC" ? "DESC" : "ASC";
            setSortBy(currentSortBy === "ASC" ? "DESC" : "ASC");
        }
        if (currentSortKey !== sort_key) {
            mySortKey = currentSortKey;
            mySortBy = "ASC";
            setSortKey(currentSortKey);
            setSortBy("ASC");
        }
        let filter = `?page=${page}&per_page=${per_Page}&sort_key=${mySortKey}&sort_by=${mySortBy}`;

        if (platformFilter) {
            let filter = platformFilter;
            filter.sort_by = mySortBy;
        }
        getPlatformList(filter);
    };

    const getPlatformList = async (platformFilter) => {
        setPlatformLists([]);
        setIsLoading(true);
        showLoader();
        try {
            const result = await platformList(platformFilter);
            if (result?.isSuccess) {
                setPlatformLists(result?.data?.data?.records);
                setTotalPages(result?.data?.data?.totalRecords);
                setPer_Page(result?.data?.pagination?.limit || 10);
                setIsLoading(false);
                hideLoader();
            } else {
                setIsLoading(false);
                hideLoader();
                setPlatformLists([]);
            }
        } catch (error) {
            setIsLoading(false);
            hideLoader();
            setPlatformLists([]);
        }
    };

    const commonPlatformFilterOptions = (platformFilterObject) => {
        let filter = `?page=${page}&per_page=${per_Page}&sort_key=${sort_key}&sort_by=${sort_by}`;

        const from_date = platformFilterObject?.from_date;
        const to_date = platformFilterObject?.to_date;
        const name = platformFilterObject?.name;
        const country = platformFilterObject?.country;
        const is_active = platformFilterObject?.is_active;

        if (new Date(from_date) <= new Date(to_date)) {
            filter += `&from_date=${filterFromDate}`;
        }

        if (new Date(to_date) >= new Date(from_date)) {
            filter += `&to_date=${filterToDate}`;
        }

        if (name && name.trim()) {
            filter += `&name=${name.replace(/'/g, "''")}`;
        }

        if (country && country.trim()) {
            filter += `&country=${country}`;
        }

        if (is_active) {
            filter += `&is_active=${is_active}`;
        } else {
            filter += `&is_active=${is_active}`;
        }

        return filter;
    }

    const handlePlatformListAddFilter = async () => {
        try {
            setFilterLoading(true);
            showLoader();
            setPage(1)
            callAPI()
        } catch (error) {
            setFilterLoading(false);
            hideLoader()
        }
    };

    const callAPI = function () {
        try {
            const filterObject = {
                from_date: filterFromDate,
                to_date: filterToDate,
                name: filterName,
                country: filterCountry,
                is_active: filterIsActive
            }

            const filter = commonPlatformFilterOptions(filterObject)
            const response = parseURLParams(filter);

            setPlatformFilter(response)
            getPlatformList(filter)
            setFirstTimeFilterActive(false)
            setFilterLoading(false);
            hideLoader();
        } catch (error) {
            setFilterLoading(false);
            hideLoader();
        }
    }

    const handlePlatformListCancelFilter = async () => {
        setFilterLoading(true);
        showLoader();
        setFilterName("");
        setFilterFromDate("");
        setFilterToDate("");
        setPlatformFilter('')
        setFilterIsActive("");
        setFilterCountry("");
        setIsRedirectFirstPage(1)
        selectInputRef.current.props.value.label = "Select";
        selectCountryInputRef.current.props.value.label = "Select";
        setFirstTimeFilterActive(false)
        setPage(1)
        const filter = `?page=${page}&per_page=${per_Page}&sort_key=${sort_key}&sort_by=${sort_by}`;
        navigate('/admin/platform/platform-list', { replace: true });
        getPlatformList(filter);
        setFilterLoading(false);
        hideLoader();
    };

    if (id !== null) {
        const response = backToPreviousClickedRecord("platform", id);
        if (response && response !== null || undefined) {
            setParams({ search: null })
        }
    }

    const handleFilterWithPagination = async () => {
        const response = commonPlatformFilterOptions(platformFilter);
        const filter = parseURLParams(response);
        const platformFilterValue = filter.from_date || filter.to_date || filter.name || filter.country || filter.is_active || filter.is_active === 'false';

        if (isRedirectFirstPage) {
            if (!firstTimeFilterActive && (platformFilterValue !== 'undefined')) {
                setPage(1)
                getPlatformList(response);
                setFirstTimeFilterActive(true);
                return;
            }

            if (firstTimeFilterActive && totalPages <= filter.per_page && (platformFilterValue)) {
                setPage(1)
                getPlatformList(response);
                return;
            }
        }

        getPlatformList(response);
    }

    useEffect(() => {
        handleFilterWithPagination();
    }, [totalPages, page, per_Page, sort_key, sort_by])

    useEffect(() => {
        const loginData = JSON.parse(localStorage.getItem("User"));
        if (loginData) {
            setPermissions(loginData.permissions);
        }
    }, []);

    const handleStatus = async (data) => {
        setIsLoading(true);
        const res = await updatePlatformStatus(data);
        toast.success(`${res.message}`);
        setIsLoading(false);
        handleFilterWithPagination();
    };

    useEffect(() => {
        if (filterName || filterFromDate || filterToDate || filterIsActive || filterCountry) {
            setIsRedirectFirstPage(0)
            if (country) {
                const countryData = countryType.filter((x) => x.name === country)
                setFilterCountry(countryData[0]?.id)
            }
            callAPI()
        }
    }, [name, fromDate, toDate, country, is_active])

    const getUrl = (baseUrl) => {
        if (filterName) {
            baseUrl += `&name=${filterName}`
        }
        if (filterCountry) {
            baseUrl += `&country=${filterCountry}`
        }
        if (filterFromDate) {
            baseUrl += `&from_date=${filterFromDate}`
        }
        if (filterToDate) {
            baseUrl += `&to_date=${filterToDate}`
        }
        if (filterIsActive || filterIsActive === false || filterIsActive === 'none') {
            baseUrl += `&is_active=${filterIsActive}`
        }
        return baseUrl;
    }

    return (
        <>
            <Header />
            <Container className="mt--7" fluid>
                <Row>
                    <div className="col">
                        <Card className="shadow ">
                            <CardHeader className="border-0">
                                <Row>
                                    <Col lg="6">
                                        <h1 className="mb-0"> Platform List</h1>
                                    </Col>
                                    <Col lg="6" className="add_btn text-right">
                                        <HasPermission slug="create-platform">
                                            <Button
                                                color="primary"
                                                onClick={() => {
                                                    navigate("/admin/platform/create-platform");
                                                }}>
                                                {isLoading && (
                                                    <span
                                                        className="indicator-progress"
                                                        style={{ display: "block" }}>
                                                        Please Wait..
                                                        <span className="spinner-border spinner-border-sm align-middle ms-3 mb-1"></span>
                                                    </span>
                                                )}
                                                {!isLoading && (
                                                    <span className="indicator-label">
                                                        Add Platform
                                                    </span>
                                                )}
                                            </Button>
                                        </HasPermission>
                                        <Button onClick={toggle}>
                                            <i className="fa-solid fa-filter"></i>
                                            <span className="me-2">Filter</span>
                                        </Button>
                                    </Col>
                                </Row>
                                <Collapse isOpen={isOpen}>
                                    <Card className=" mt-2">
                                        <CardBody>
                                            <Row>
                                                <Col lg="3">
                                                    <FormGroup>
                                                        <label className="form-control-label">
                                                            From Date
                                                        </label>
                                                        <Input
                                                            closeonselect="true"
                                                            className="form-control"
                                                            id="from_date"
                                                            name="from_date"
                                                            type="date"
                                                            value={filterFromDate}
                                                            onChange={(e) => {
                                                                setFilterFromDate(e.target.value);
                                                            }}
                                                            onKeyDown={(e) => handleFilterOnKeyEnter(e, handlePlatformListAddFilter, filterFromDate)}
                                                        />
                                                    </FormGroup>
                                                </Col>
                                                <Col lg="3">
                                                    <FormGroup>
                                                        <label className="form-control-label">
                                                            To Date
                                                        </label>
                                                        <Input
                                                            closeonselect="true"
                                                            className="form-control"
                                                            id="to_date"
                                                            name="to_date"
                                                            type="date"
                                                            value={filterToDate}
                                                            onChange={(e) => {
                                                                setFilterToDate(e.target.value);
                                                            }}
                                                            onKeyDown={(e) => handleFilterOnKeyEnter(e, handlePlatformListAddFilter, filterToDate)}
                                                        />
                                                    </FormGroup>
                                                </Col>
                                                <Col lg="3">
                                                    <FormGroup>
                                                        <label className="form-control-label">Name</label>
                                                        <Input
                                                            id="name"
                                                            name="name"
                                                            placeholder="Name"
                                                            type="text"
                                                            value={filterName}
                                                            onChange={(event) => {
                                                                setFilterName(event.target.value);
                                                            }}
                                                            onKeyDown={(e) => handleFilterOnKeyEnter(e, handlePlatformListAddFilter, filterName)}
                                                        />
                                                    </FormGroup>
                                                </Col>
                                                <Col lg="3">
                                                    <FormGroup>
                                                        <label className="form-control-label">
                                                            Country
                                                        </label>
                                                        <Select
                                                            name="country"
                                                            id="country"
                                                            defaultValue={{
                                                                label: filterCountry !== 'none' && filterCountry !== '' || null ? filterCountry : filterCountry == 'none' ? 'None' : 'Select',
                                                            }}
                                                            options={Array.from([{ id: 'none', name: 'None' }, ...countryType]).map(
                                                                (value) => {
                                                                    return {
                                                                        value: value.id,
                                                                        label: value.name,
                                                                    };
                                                                }
                                                            )}
                                                            onChange={(event) => {
                                                                setFilterCountry(event.value);
                                                            }}
                                                            onKeyDown={(e) => handleFilterOnKeyEnter(e, handlePlatformListAddFilter, filterCountry)}
                                                            ref={selectCountryInputRef}
                                                        />
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col lg="3">
                                                    <FormGroup>
                                                        <label className="form-control-label">
                                                            Is Active
                                                        </label>
                                                        <Select
                                                            name="is_active"
                                                            id="is_active"
                                                            className="react-select"
                                                            defaultValue={{
                                                                label: filterIsActive === "true" ? "Yes" : filterIsActive === "false" ? "No" : filterIsActive == 'none' ? 'None' : 'Select',
                                                            }}
                                                            options={Array.from([{ id: 'none', name: 'None' }, ...isActive]).map((value) => {
                                                                return {
                                                                    value: value.id,
                                                                    label: value.name,
                                                                };
                                                            })}
                                                            onChange={(event) => {
                                                                setFilterIsActive(event.value);
                                                            }}
                                                            onKeyDown={(e) => handleFilterOnKeyEnter(e, handlePlatformListAddFilter, filterIsActive)}
                                                            ref={selectInputRef}
                                                        />
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col lg="3">
                                                    <Button
                                                        color="primary"
                                                        type="submit"
                                                        onClick={handlePlatformListAddFilter}
                                                        className="filter my-3 w-100">
                                                        {filterLoading && (
                                                            <span
                                                                className="indicator-progress"
                                                                style={{ display: "block" }}>
                                                                Please Wait..
                                                                <span className="spinner-border spinner-border-sm align-middle ms-3 mb-1"></span>
                                                            </span>
                                                        )}
                                                        {!filterLoading && (
                                                            <span className="indicator-label">
                                                                ADD FILTER
                                                            </span>
                                                        )}
                                                    </Button>
                                                </Col>
                                                <Col lg="3">
                                                    <Button
                                                        color="primary"
                                                        type="submit"
                                                        onClick={handlePlatformListCancelFilter}
                                                        className="filter my-3 w-100">
                                                        {filterLoading && (
                                                            <span
                                                                className="indicator-progress"
                                                                style={{ display: "block" }}>
                                                                Please Wait..
                                                                <span className="spinner-border spinner-border-sm align-middle ms-3 mb-1"></span>
                                                            </span>
                                                        )}
                                                        {!filterLoading && (
                                                            <span className="indicator-label">
                                                                RESET FILTER
                                                            </span>
                                                        )}
                                                    </Button>
                                                </Col>
                                            </Row>
                                        </CardBody>
                                    </Card>
                                </Collapse>
                            </CardHeader>
                            <CardBody>
                                <Row>
                                    <Col>
                                        <div
                                            className="table-responsive p-0 pb-2 fs-6"
                                            id="data-table">
                                            <table className="table align-items-center display justify-content-center mb-0 w-100">
                                                <thead>
                                                    <tr>
                                                        <th >No.</th>
                                                        <th
                                                            style={{ cursor: "pointer" }}
                                                            onClick={() => sortColumns("name", sort_by)}>
                                                            {" "}
                                                            Name
                                                            {sort_key === "name" &&
                                                                (sort_by === "ASC" ? (
                                                                    <i className="fa fa-arrow-up ml-1"></i>
                                                                ) : (
                                                                    <i className="fa fa-arrow-down ml-1"></i>
                                                                ))}{" "}
                                                        </th>
                                                        <th
                                                            style={{ cursor: "pointer" }}
                                                            onClick={() =>
                                                                sortColumns("country", sort_by)
                                                            }>
                                                            Country
                                                            {sort_key === "country" &&
                                                                (sort_by === "ASC" ? (
                                                                    <i className="fa fa-arrow-up ml-1"></i>
                                                                ) : (
                                                                    <i className="fa fa-arrow-down ml-1"></i>
                                                                ))}
                                                        </th>
                                                        <th>Weblink</th>
                                                        <th
                                                            style={{ cursor: "pointer" }}
                                                            onClick={() =>
                                                                sortColumns("created_at", sort_by)
                                                            }>
                                                            CreatedAt
                                                            {sort_key === "created_at" &&
                                                                (sort_by === "ASC" ? (
                                                                    <i className="fa fa-arrow-up ml-1"></i>
                                                                ) : (
                                                                    <i className="fa fa-arrow-down ml-1"></i>
                                                                ))}{" "}
                                                        </th>
                                                        <HasPermission slug="update-platform">
                                                            <th
                                                                style={{ cursor: "pointer" }}
                                                                onClick={() =>
                                                                    sortColumns("is_active", sort_by)
                                                                }>
                                                                Is Active
                                                                {sort_key === "is_active" &&
                                                                    (sort_by === "ASC" ? (
                                                                        <i className="fa fa-arrow-up ml-1"></i>
                                                                    ) : (
                                                                        <i className="fa fa-arrow-down ml-1"></i>
                                                                    ))}{" "}
                                                            </th>{" "}
                                                            <th className="action">Action</th>
                                                        </HasPermission>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {platformLists.length > 0 ? (
                                                        platformLists.map((info, index) => {
                                                            const indexNo =
                                                                (page - 1) * per_Page + (index + 1);
                                                            return (
                                                                <tr key={info.id} id={`platform-list-record-${info.id}`}>
                                                                    <td>{indexNo}</td>
                                                                    <td>{info.name}</td>
                                                                    <td>{info.country}</td>
                                                                    <td>
                                                                        {info.weblink ? (
                                                                            <a href={info.weblink} target="_blank" rel="noopener noreferrer">
                                                                                {info.weblink}
                                                                            </a>
                                                                        ) : ""}
                                                                    </td>
                                                                    <td>
                                                                        {info.created_at
                                                                            ? moment(info.created_at).format(
                                                                                "YYYY-MM-DD HH:mm:ss"
                                                                            )
                                                                            : ""}
                                                                    </td>
                                                                    <HasPermission slug="update-platform">
                                                                        <td>
                                                                            <label className="custom-toggle">
                                                                                <input
                                                                                    onChange={() => { }}
                                                                                    onClick={() => {
                                                                                        handleStatus({
                                                                                            id: info.id,
                                                                                            is_active: !info.is_active,
                                                                                        });
                                                                                    }}
                                                                                    value={info.is_active === true}
                                                                                    defaultChecked={
                                                                                        info.is_active === true
                                                                                    }
                                                                                    type="checkbox"
                                                                                />
                                                                                <span className="custom-toggle-slider  rounded-circle" />
                                                                            </label>
                                                                        </td>
                                                                        <td className="action">
                                                                            <Button
                                                                                color="primary"
                                                                                onClick={async () => {
                                                                                    const res =
                                                                                        await getPlatformById({
                                                                                            platform_id: info.id,
                                                                                        });
                                                                                    let baseURL = `/admin/platform/update-platform/${info.id}?page=${page}`;
                                                                                    const url = getUrl(baseURL);
                                                                                    navigate(
                                                                                        url,
                                                                                        { state: { row: res?.data } }
                                                                                    );
                                                                                }}>
                                                                                {isLoading && (
                                                                                    <span
                                                                                        className="indicator-progress"
                                                                                        style={{ display: "block" }}>
                                                                                        Please Wait..
                                                                                        <span className="spinner-border spinner-border-sm align-middle ms-3 mb-1"></span>
                                                                                    </span>
                                                                                )}
                                                                                {!isLoading && (
                                                                                    <span className="indicator-label">
                                                                                        Edit
                                                                                    </span>
                                                                                )}
                                                                            </Button>
                                                                        </td>
                                                                    </HasPermission>
                                                                </tr>
                                                            );
                                                        })
                                                    ) : (
                                                        <tr>
                                                            <td className="text-center" colSpan={7}>
                                                                No Result Found
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </Col>
                                </Row>
                            </CardBody>
                            <CardFooter className="py-4">
                                <Paginationes
                                    className="pagination justify-content-end mb-0"
                                    current={page}
                                    pageSize={per_Page}
                                    total={totalPages}
                                    onChange={(value) => setPage(value)}
                                />
                            </CardFooter>
                        </Card>
                    </div>
                </Row>
            </Container>
            {loader}
        </>
    );
}

export default PlatformList;
